import React, { memo, Suspense, useCallback, useMemo, useState } from 'react'
import { HStack } from '../../../../../../ui/Stack'
import { ModalCreateTask } from 'src/react-app/views/modal-create-task-new'
import { ModalAddNote } from '../../../../../../views/modal-add-notes-new'
import { getAppealsFormData } from '../../../selectors'
import { useSelector } from 'react-redux'
import {
  CardActions,
  useCardActions,
  CardActionConfig,
} from '../../../../../../components/v2/card-actions'
import { HSteps } from '../../../../../../ui/Steps'
import { MargeModal } from '../../components/marge-modal/marge-modal'

interface AppealsFormsHeaderProps {
  id: string | number
  className?: string
  stage?: number
  isFinished?: boolean
  toOrder?: () => void
  toClaim?: () => void
  isFormProcessing?: boolean
}

const titlesButton = ['Взять в работу']

const AppealsFormsHeader: React.FC<AppealsFormsHeaderProps> = memo(
  (props: AppealsFormsHeaderProps) => {
    const { id, stage = 0, toOrder, isFormProcessing = false } = props
    const isDisabledButtons = id === 'new'
    const [isOpenModalCreateTask, setIsOpenModalCreateTask] = useState<boolean>(false)
    const [isOpenModalAddNote, setIsOpenModalAddNote] = useState<boolean>(false)
    const [isOpenMargeModal, setIsOpenMargeModal] = useState<boolean>(false)
    const appealsFormData = useSelector(getAppealsFormData)
    const isDisabledByStage = appealsFormData?.stages?.current !== 0

    const handleNextStageOnClick = useCallback(async () => {
      if (toOrder) {
        await toOrder?.()
      }
    }, [toOrder])

    const onCloseAddNote = useCallback(() => {
      setIsOpenModalAddNote(false)
    }, [setIsOpenModalAddNote])

    const onCloseCreateTask = useCallback(() => {
      setIsOpenModalCreateTask(false)
    }, [setIsOpenModalCreateTask])

    const onCloseMargeModal = useCallback(() => {
      setIsOpenMargeModal(false)
    }, [setIsOpenMargeModal])

    const actionsConfig = useMemo(
      (): CardActionConfig[] => [
        {
          key: 'next-stage',
          label: titlesButton[stage] || '',
          type: 'primary',
          onClick: handleNextStageOnClick,
          isPrimary: true,
          condition: Boolean(titlesButton[stage]),
          disabled: id === 'new' || isFormProcessing,
          loading: isFormProcessing,
          order: 1,
        },
        {
          key: 'add-note',
          label: 'Добавить заметку',
          onClick: () => setIsOpenModalAddNote(true),
          condition: !isDisabledButtons,
          order: 10,
          disabled: isFormProcessing,
        },
        {
          key: 'create-task',
          label: 'Создать задачу',
          onClick: () => setIsOpenModalCreateTask(true),
          condition: !isDisabledButtons,
          order: 20,
          disabled: isFormProcessing,
        },
        {
          key: 'merge-customers',
          label: 'Объединить клиентов',
          onClick: () => setIsOpenMargeModal(true),
          condition: !isDisabledButtons && !isDisabledByStage,
          order: 30,
          disabled: isFormProcessing,
        },
      ],
      [stage, isDisabledButtons, isDisabledByStage, handleNextStageOnClick, isFormProcessing],
    )

    const { primaryAction, secondaryActions } = useCardActions({ actions: actionsConfig })

    return (
      <>
        <HStack gap={'8'} className={'mapWrapper'} wrap>
          <div>
            {appealsFormData?.stages && (
              <HSteps
                items={Object.values(appealsFormData?.stages?.items)}
                current={appealsFormData?.stages?.current}
                history={appealsFormData?.stages?.history}
              />
            )}
          </div>
          <CardActions primaryAction={primaryAction} secondaryActions={secondaryActions} />
        </HStack>
        <ModalAddNote
          entityId={Number(id)}
          entityType={'case'}
          isOpen={isOpenModalAddNote}
          onClose={onCloseAddNote}
        />
        <Suspense fallback={''}>
          <ModalCreateTask
            isOpen={isOpenModalCreateTask}
            onClose={onCloseCreateTask}
            entityType={'case'}
            entityId={id}
          />
        </Suspense>
        <MargeModal
          isOpen={isOpenMargeModal}
          onClose={onCloseMargeModal}
          appealId={id}
          warningContent={undefined}
        />
      </>
    )
  },
)

AppealsFormsHeader.displayName = 'AppealsFormsHeader'

export default AppealsFormsHeader
